import { css } from '@emotion/react'
import { SyntheticEvent, memo, useCallback, useMemo, useState } from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { goToNotes } from '../../../../actions/nav'
import { isTouchDevice } from '../../../../utilities/browser'
// import { hexRGBA } from '../../../../utilities/colors'
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu'
// import { useModelColor } from '../../../Hooks/useModelColor'
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../actions/room'
import {
    TransitionTypes,
    motionTransition,
} from '../../../../utilities/transitions'
import { useIsRoomPinned } from '../../../Hooks/useRoom'
import { useUserSelector } from '../../../Hooks/useUser'
import RoomIcon from '../../../Icons/Room'
import { palette } from '../../../styles/colors'
import layers from '../../../styles/layers'
import shape from '../../../styles/shape'
import {
    hideDragHelper,
    highlightDragHelper,
    showDragHelper,
} from '../../Chat/Rooms/PinnedRooms/PinDragHelper'
import BookmarkBadge from '../Badges/Bookmark'
import FriendBadge from '../Badges/Friend'
import MissMFCBadge from '../Badges/MissMFC'
import NewBadge from '../Badges/New'
import PopularBadge from '../Badges/Popular'
import PriorMissMFCBadge from '../Badges/PriorMissMFC'
import StatusBadge from '../Badges/Status'
import TrendingBadge from '../Badges/Trending'
import GroupShowBanner from '../Banners/GroupShow'
import MissMFCBanner from '../Banners/MissMFC'
import NewModelBanner from '../Banners/NewModel'
import PopularBanner from '../Banners/Popular'
import TrendingBanner from '../Banners/Trending'
import NotesButton from '../Buttons/NotesButton'
import PinButton from '../Buttons/PinButton'
import Container from '../Container'
import LargeFloatingPreview from '../LargeFloatingPreview'
import ModelName from '../ModelName'
import {
    GetStylesConfig,
    getAnonymousMissMFCRank,
    isAnonymousMissMFC,
    resolveDisplayHighlight,
    resolveModelStatus,
} from '../helpers'

const rootStyles = css({
    borderRadius: shape.borderRadiusSmallMedium,
    justifyContent: 'center',
    flexDirection: 'column',
    boxShadow: layers.one,
    border: palette.layerLightBorder,
    position: 'relative',
    alignItems: 'center',
    overflow: 'hidden',
    display: 'flex',
    height: '100%',
})

const textContainerStyles = css({
    justifyContent: 'space-between',
    display: 'flex',
    width: '100%',
})
const newBadgeStyles = css({
    justifyContent: 'center',
    display: 'flex',
    width: '100%',
})
const bannerContainerStyles = css({
    borderTop: `1px solid ${palette.drawerDivider}`,
    width: '100%',
})
const statusStyles = css({
    justifyContent: 'space-around',
    alignItems: 'center',
    display: 'flex',
    width: '100%',
})
const badgeContainerStyles = css({
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    display: 'flex',
    width: '100%',
})
const missMfcContainerStyles = css({
    color: palette.backgroundReverse,
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 'bold',
    padding: '0 2px',
    display: 'flex',
    opacity: 0.8,
    marginTop: 2,
})

interface TextProps {
    activeRoom?: number
    columnWidth: number
    disableMenuNextPrev?: boolean
    displayBookmarkBadge?: boolean
    displayBookmarkHighlight?: boolean
    displayFriendBadge?: boolean
    displayFriendHighlight?: boolean
    displayGroupShowBanner?: boolean
    displayMissMFCBadge?: boolean
    displayMissMFCBanner?: boolean
    displayNewModelBadge?: boolean
    displayNewModelBanner?: boolean
    displayNoteIconButton?: boolean
    displayPopularBadge?: boolean
    displayPopularBanner?: boolean
    displayPopularHighlight?: boolean
    displayRecentlyVisitedBadge?: boolean
    displayRecentlyVisitedHighlight?: boolean
    displayStatusBadge?: boolean
    displayTrendingBadge?: boolean
    displayTrendingBanner?: boolean
    displayTrendingHighlight?: boolean
    enableLargePreview?: boolean
    enablePin?: boolean
    enablePinToTopSymbols?: boolean
    getStyle: (key: string, config?: GetStylesConfig) => any
    gridPadding: number
    id: number
    loungeSlug?: string
    name: string
    singlePin?: boolean
    pinLimitReached?: boolean
}

const Text = memo((props: TextProps) => {
    const {
        displayRecentlyVisitedHighlight,
        displayRecentlyVisitedBadge,
        displayTrendingHighlight,
        displayBookmarkHighlight,
        displayPopularHighlight,
        displayFriendHighlight,
        displayGroupShowBanner,
        displayTrendingBanner,
        displayNewModelBanner,
        displayPopularBanner,
        displayMissMFCBanner,
        displayBookmarkBadge,
        displayTrendingBadge,
        displayNewModelBadge,
        displayPopularBadge,
        displayMissMFCBadge,
        displayStatusBadge,
        displayFriendBadge,
        enableLargePreview,
        displayNoteIconButton,
        enablePinToTopSymbols,
        disableMenuNextPrev,
        pinLimitReached,
        gridPadding,
        columnWidth,
        activeRoom,
        loungeSlug,
        singlePin,
        enablePin,
        getStyle,
        name,
        id,
    } = props

    const [showMenu, setShowMenu] = useState(false)
    const isPinnedToTopRecentlyVisited = useUserSelector(
        id,
        'isPinnedToTopRecentlyVisited',
    )
    const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending')
    const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular')
    const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC')
    const isBanned = useUserSelector(id, 'banned')
    const isGroupShowPending = useUserSelector(id, 'isGroupShowPending')
    const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank')
    const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')
    const isTrendingNew = useUserSelector(id, 'isTrendingNew')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const trendingDiff = useUserSelector(id, 'trendingDiff')
    const missMFCPrize = useUserSelector(id, 'missMFCPrize')
    const isPublicShow = useUserSelector(id, 'isPublicShow')
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isAnonymous = isAnonymousMissMFC(id)
    const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank')
    const missMFCRank = isAnonymous
        ? getAnonymousMissMFCRank(name)
        : missMFCRankFromSelector
    const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC')
    const isMissMFC = isAnonymous ? true : isMissMFCFromSelector
    const isBookmark = useUserSelector(id, 'isBookmark')
    const isNewModel = useUserSelector(id, 'isNewModel')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isPopular = useUserSelector(id, 'isPopular')
    const isOffline = useUserSelector(id, 'isOffline')
    const isKicked = useUserSelector(id, 'isKicked')
    const isFriend = useUserSelector(id, 'isFriend')
    const creation = useUserSelector(id, 'creation')
    const viewers = useUserSelector(id, 'viewers')
    const hasNote = useUserSelector(id, 'hasNote')
    const isAway = useUserSelector(id, 'isAway')
    const color = useUserSelector(id, 'color')
    const slug = useUserSelector(id, 'slug')

    const stylesUtilitiesPX = getStyle('utilities', { px: true })
    const stylesFooterPX = getStyle('footer', { px: true })
    const stylesBannerPX = getStyle('banner', { px: true })
    const stylesNamePX = getStyle('name', { px: true })
    const stylesUtilities = getStyle('utilities')
    const stylesBanner = getStyle('banner')
    const stylesBannerPopular = stylesBanner('popular')
    const stylesBannerPopularViewers = stylesBannerPopular('viewers')
    const stylesBannerTrending = stylesBanner('trending')
    const stylesBannerTrendingDiff = stylesBannerTrending('diff')
    const stylesBannerNew = stylesBanner('new')
    const stylesBannerNewTime = stylesBannerNew('time')
    const stylesBannerMissMFC = stylesBanner('missMFC')
    const stylesBannerMissMFCRank = stylesBannerMissMFC('rank')
    const stylesBadge = getStyle('badge')
    const stylesBadgeStatus = stylesBadge('status')
    const stylesBadgeMissMFC = stylesBadge('missMFC')
    const stylesBadgePriorMissMFC = stylesBadge('priorMissMFC')
    const stylesBadgeNew = stylesBadge('new')
    const stylesSection1 = getStyle('section1', { px: true })

    const modelStatus = resolveModelStatus({
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isAway,
    })

    const onOpenMenuCallback = useCallback(() => {
        openMenuDialog(slug, {
            hideNextPrev: disableMenuNextPrev,
            transitionSource: TransitionTypes.TILE,
        })
    }, [slug, disableMenuNextPrev])

    const onGoToRoomCallback = useCallback(
        (event: SyntheticEvent) => {
            console.log('[Text] onGoToRoomCallback', id, event.target)
            event.stopPropagation()
            if (isAnonymous) return
            if (loungeSlug) {
                // if we're in the lounge, set the surrogate
                setLoungeSurrogate(id, loungeSlug)
            } else {
                openRoom(slug, {
                    transitionSource: TransitionTypes.TILE,
                })
            }
        },
        [isAnonymous, id, slug, loungeSlug],
    )

    const isRoomPinned = useIsRoomPinned(id)

    // if (!id) {
    //     return <AvatarSkeleton width={width} height={height} />
    // }

    // const modelColor = useModelColor(color)

    //
    // For testing purposes:
    //
    // const candisplayFriendBadge = true
    // const canDisplayBookmarkBadge = true
    // const canDisplayTrendingBadge = true
    // const canDisplayPopularBadge = true
    // const canDisplayStatusBadge = true
    // const canDisplayNewModelBadge = true
    // const canDisplayMissMFCBadge = true
    // const canDisplayPriorMissMFCBadge = true
    // const canDisplayRecentlyVisitedBadge = true
    // const canDisplayNotesButton = false
    // const canEnablePinToTopSymbols = false
    // const canDisplayLargePreviewButton = true
    // const canDisplayPinButton = true

    //
    // Symbols
    //
    const canEnablePinToTopSymbols = enablePinToTopSymbols

    const dicanDisplayGroupShowBanner =
        displayGroupShowBanner &&
        (isGroupShow || isGroupShowPending || groupShowRoomCount)
    const canDisplayTrendingBanner =
        displayTrendingBanner && isTrendingNew && trendingDiff
    const canDisplayNewModelBanner = displayNewModelBanner && isNewModel
    const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers
    const canDisplayMissMFCBanner =
        displayMissMFCBanner && isMissMFC && missMFCRank

    const candisplayFriendBadge = displayFriendBadge && isFriend
    const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark

    const canDisplayTrendingBadge =
        displayTrendingBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending) &&
        isTrendingNew &&
        trendingDiff
    const canDisplayPopularBadge =
        displayPopularBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular) &&
        isPopular &&
        viewers
    const canDisplayNewModelBadge = displayNewModelBadge && isNewModel
    const canDisplayMissMFCBadge =
        displayMissMFCBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopMissMFC) &&
        isMissMFC &&
        missMFCRank > 0
    const canDisplayPriorMissMFCBadge = !!priorMissMfcRank
    const canDisplayStatusBadge = displayStatusBadge && !!modelStatus
    const canDisplayRecentlyVisitedBadge =
        displayRecentlyVisitedBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    //
    // Other
    //
    const canShowMenu = !isAnonymous && (isTouchDevice || showMenu)
    const availableWidth = columnWidth - gridPadding

    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway
    const isViewable = !noShowType && !isBanned && !isKicked
    const showSinglePin = singlePin && isViewable

    //
    // Buttons
    //
    const canDisplayNotesButton = hasNote && displayNoteIconButton
    const canDisplayLargePreviewButton =
        enableLargePreview && isPublicShow && !isAnonymous
    const canDisplayPinButton =
        ((enablePin && !pinLimitReached) || isRoomPinned || showSinglePin) &&
        !isAnonymous
    //
    // Highlights
    //
    const canDisplayActiveRoomHighlight = activeRoom === id
    const canDisplayFriendHighlight = displayFriendHighlight && isFriend
    const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark
    const canDisplayPopularHighlight =
        displayPopularHighlight &&
        isPopular &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular)
    const canDisplayTrendingHighlight =
        displayTrendingHighlight &&
        isTrendingNew &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending)
    const canDisplayRecentlyViewedHighlight =
        displayRecentlyVisitedHighlight &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    const highlight = resolveDisplayHighlight({
        displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
        displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
        displayTrendingHighlight: canDisplayTrendingHighlight,
        displayBookmarkHighlight: canDisplayBookmarkHighlight,
        displayPopularHighlight: canDisplayPopularHighlight,
        displayFriendHighlight: canDisplayFriendHighlight,
    })

    const onPinCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            motionTransition(() => pinRoom(id, { pinned: !isRoomPinned }), {
                id,
                skipTransition: isRoomPinned, // skip if we're unpinning
                transitionSource: TransitionTypes.TILE,
                transitionDestination: TransitionTypes.PINROOM,
            })
        },
        [id, isRoomPinned],
    )

    const onOpenNote = useCallback(
        (event) => {
            event.stopPropagation()
            goToNotes(name)
        },
        [id, name],
    )

    const canDisplayBanners =
        dicanDisplayGroupShowBanner ||
        canDisplayTrendingBanner ||
        canDisplayNewModelBanner ||
        canDisplayMissMFCBanner ||
        canDisplayPopularBanner

    const canDisplayBadges =
        canDisplayBookmarkBadge ||
        canDisplayTrendingBadge ||
        canDisplayMissMFCBadge ||
        canDisplayPopularBadge ||
        canDisplayStatusBadge ||
        candisplayFriendBadge

    const canDisplayUtilities =
        canDisplayRecentlyVisitedBadge ||
        canDisplayLargePreviewButton ||
        canDisplayNotesButton ||
        canDisplayPinButton

    const badgeCount = useMemo(
        () =>
            [
                Number(canDisplayBookmarkBadge),
                Number(canDisplayTrendingBadge),
                Number(canDisplayPopularBadge),
                Number(candisplayFriendBadge),
            ].reduce((memo, active) => memo + active, 0),
        [
            canDisplayTrendingBadge,
            canDisplayBookmarkBadge,
            canDisplayPopularBadge,
            candisplayFriendBadge,
        ],
    )

    const buttonCount = useMemo(
        () =>
            [
                Number(canDisplayRecentlyVisitedBadge),
                Number(canDisplayLargePreviewButton),
                Number(canDisplayNotesButton),
                Number(canDisplayPinButton),
            ].reduce((memo, active) => memo + active, 0),
        [
            canDisplayRecentlyVisitedBadge,
            canDisplayLargePreviewButton,
            canDisplayNotesButton,
            canDisplayPinButton,
        ],
    )

    const dragStart = useCallback(
        (event) => {
            // event.dataTransfer.setData('text/plain', String(id))
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', id)
            showDragHelper(id)
        },
        [id],
    )
    const dragMove = useCallback((event) => {
        const { clientX, clientY } = event
        highlightDragHelper(clientX, clientY)
    }, [])
    const dragEnd = useCallback(() => {
        hideDragHelper()
    }, [])
    return (
        <div
            data-testid="Container__ModelDisplay"
            onClick={onGoToRoomCallback}
            onMouseOver={isTouchDevice ? null : () => setShowMenu(true)}
            onMouseLeave={isTouchDevice ? null : () => setShowMenu(false)}
            css={rootStyles}
            style={{
                border: highlight ? `1px solid ${highlight}` : null,
            }}
            key={id}
            id={`tile-${id}`}
            draggable
            onDragStart={dragStart}
            onDrag={dragMove}
            onDragEnd={dragEnd}
        >
            <div
                data-testid="Container__Text"
                css={textContainerStyles}
                style={{
                    visibility: canDisplayUtilities ? null : 'hidden',
                    height: stylesUtilitiesPX('height'),
                }}
            >
                {canDisplayLargePreviewButton && (
                    <LargeFloatingPreview
                        shadow={false}
                        color={palette.backgroundReverse}
                        size={stylesUtilities('iconSize')}
                        id={id}
                    />
                )}
                {canDisplayNotesButton && (
                    <NotesButton
                        size={stylesUtilities('iconSize')}
                        color={palette.backgroundReverse}
                        filled
                        onClick={onOpenNote}
                        title="Open Notes"
                        shadow={false}
                    />
                )}
                {canDisplayRecentlyVisitedBadge && (
                    <Container
                        title="Recently Visited"
                        styleOverrides={{ padding: 8 }}
                        shadow={false}
                    >
                        <RoomIcon
                            size={stylesUtilities('iconSize')}
                            color={palette.backgroundReverse}
                            filled
                        />
                    </Container>
                )}
                {canDisplayPinButton && (
                    <PinButton
                        title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'}
                        size={stylesUtilities('iconSize')}
                        color={palette.backgroundReverse}
                        filled={isRoomPinned}
                        containerStyles={{
                            marginLeft: buttonCount === 1 ? 'auto' : null,
                        }}
                        shadow={false}
                        onClick={onPinCallback}
                    />
                )}
            </div>
            <div
                css={newBadgeStyles}
                style={{
                    visibility: canDisplayNewModelBadge ? null : 'hidden',
                    fontSize: stylesBadgeStatus('fontSize'),
                    height: stylesBadgeNew('height'),
                }}
            >
                <NewBadge styleOverrides={{ padding: 0 }} />
            </div>
            <ModelName
                modelId={id}
                styleOverrides={{
                    fontSize: stylesNamePX('fontSize')(),
                    height: stylesNamePX('height'),
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingBottom: 0,
                    width: '100%',
                }}
                isAnonymous={isAnonymous}
                color={color}
                name={name}
            />
            <div
                css={bannerContainerStyles}
                style={{
                    visibility: canDisplayBanners ? null : 'hidden',
                    height: stylesBannerPX('height'),
                }}
            >
                {canDisplayMissMFCBanner && (
                    <MissMFCBanner
                        styleOverrides={{
                            fontSize: stylesBannerPX('fontSize'),
                            height: stylesBannerPX('height'),
                            padding: '0 3px',
                        }}
                        rankStyleOverrides={{
                            margin: stylesBannerMissMFCRank('margin'),
                        }}
                        text={stylesBannerMissMFC('text')}
                        missMFCPrize={missMFCPrize}
                        missMFCRank={missMFCRank}
                    />
                )}
                {canDisplayTrendingBanner && (
                    <TrendingBanner
                        styleOverrides={{
                            fontSize: stylesBannerPX('fontSize'),
                            height: stylesBannerPX('height'),
                        }}
                        diffStyleOverrides={{
                            margin: stylesBannerTrendingDiff('margin'),
                        }}
                        text={stylesBannerTrending('text')}
                        hideIcon={availableWidth < stylesBanner('minimumWidth')}
                        diff={trendingDiff}
                    />
                )}
                {canDisplayPopularBanner && (
                    <PopularBanner
                        styleOverrides={{
                            fontSize: stylesBannerPX('fontSize'),
                            height: stylesBannerPX('height'),
                        }}
                        viewersStyleOverrides={{
                            margin: stylesBannerPopularViewers('margin'),
                        }}
                        iconSize={stylesBanner('iconSize')}
                        text={stylesBannerPopular('text')}
                        hideIcon={availableWidth < stylesBanner('minimumWidth')}
                        viewers={viewers}
                    />
                )}
                {dicanDisplayGroupShowBanner &&
                    (isGroupShow ||
                        isGroupShowPending ||
                        groupShowRoomCount) && (
                        <GroupShowBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                                padding: '0 3px',
                            }}
                            iconSize={stylesBanner('iconSize')}
                            roomCount={groupShowRoomCount}
                            isLive={isGroupShow}
                        />
                    )}
                {canDisplayNewModelBanner && (
                    <NewModelBanner
                        styleOverrides={{
                            fontSize: stylesBannerPX('fontSize'),
                            height: stylesBannerPX('height'),
                            color: palette.backgroundReverse,
                        }}
                        timeStyleOverrides={{
                            margin: stylesBannerNewTime('margin'),
                        }}
                        compact={columnWidth < 135}
                        creation={creation}
                    />
                )}
            </div>
            <div
                css={statusStyles}
                style={{
                    visibility: canDisplayStatusBadge ? null : 'hidden',
                    height: stylesSection1('height'),
                }}
            >
                {!!canDisplayStatusBadge && (
                    <StatusBadge
                        styleOverrides={{
                            fontSize: stylesBadgeStatus('fontSize'),
                            color: palette.backgroundReverse,
                            overflow: 'hidden',
                            background: 'unset',
                            width: 'auto',
                        }}
                        status={modelStatus}
                    />
                )}
            </div>
            <div
                css={badgeContainerStyles}
                style={{
                    visibility:
                        canDisplayBadges || canShowMenu ? null : 'hidden',
                    height: stylesFooterPX('height'),
                }}
            >
                <div
                    style={{
                        marginBottom: stylesBadgeStatus('offsetY'),
                        marginLeft: stylesBadgeStatus('offsetX'),
                        flexDirection: 'column',
                        display: 'flex',
                    }}
                >
                    {(candisplayFriendBadge ||
                        canDisplayBookmarkBadge ||
                        canDisplayTrendingBadge ||
                        canDisplayPopularBadge ||
                        canDisplayPriorMissMFCBadge) && (
                        <div
                            style={{
                                justifyContent:
                                    badgeCount === 1 ? 'flex-start' : 'center',
                                display: 'flex',
                            }}
                        >
                            {candisplayFriendBadge && (
                                <FriendBadge
                                    size={stylesBadgeStatus('iconSize')}
                                    shadow={false}
                                />
                            )}
                            {canDisplayBookmarkBadge && (
                                <BookmarkBadge
                                    size={stylesBadgeStatus('iconSize')}
                                    shadow={false}
                                />
                            )}
                            {canDisplayPopularBadge && (
                                <PopularBadge
                                    size={stylesBadgeStatus('iconSize')}
                                    shadow={false}
                                />
                            )}
                            {canDisplayTrendingBadge && (
                                <TrendingBadge
                                    size={stylesBadgeStatus('iconSize')}
                                    shadow={false}
                                />
                            )}
                            {canDisplayPriorMissMFCBadge && (
                                <PriorMissMFCBadge
                                    styleOverrides={{
                                        height: stylesBadgePriorMissMFC(
                                            'height',
                                        ),
                                        width: stylesBadgePriorMissMFC('width'),
                                    }}
                                    containerProps={{
                                        styleOverrides: {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                    priorMissMfcRank={priorMissMfcRank}
                                    shadow={false}
                                />
                            )}
                        </div>
                    )}
                    {canDisplayMissMFCBadge && (
                        <div style={{ display: 'flex' }}>
                            <div
                                css={missMfcContainerStyles}
                                style={{
                                    marginRight: stylesBadgeMissMFC('compact')
                                        ? 0
                                        : 1,
                                    fontSize: stylesBadgeStatus('fontSize'),
                                }}
                            >
                                <MissMFCBadge
                                    compact={stylesBadgeMissMFC('compact')}
                                    missMFCRank={missMFCRank}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div
                    style={{
                        visibility: canShowMenu ? null : 'hidden',
                        height: stylesFooterPX('height'),
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <ButtonModelMenu
                        styleOverrides={css({
                            margin: stylesFooterPX('margin'),
                        })}
                        onClick={onOpenMenuCallback}
                    />
                </div>
            </div>
        </div>
    )
})

export default Text
export { Text }
